import React, { useState, useEffect, useMemo } from "react";
import { useParams, useLocation } from "react-router-dom";
import reduxStore from "reduxstore/reduxStore";
import i18n from "i18n/i18n";
import fleetlogicAPI from "helpers/fleetlogicAPI";
import Uri from "jsuri";
import { AuthorizationToken } from "helpers/AuthorizationToken";
import { Button, Select, useConfirmModal, Radio, Input } from "@texada/ui";
import {
  TIS_SESSION_VERIFY_URL,
  USER_URL,
  COMPANY_PERMISSION_URL,
  USER_PERMISSION_URL,
  HEADOFFICE_OPTION_VALUE,
  APP_DEFAULT_REDIRECT_URL,
  SCS_SRM_UID
} from "constants/AppConstants";
import SRMSyncLoader from "./SRMSyncLoader";
import {
  ReduxState,
  filterSortMapOptions,
  isPlayEnv,
  SrmBoolean
} from "helpers/helper";
import {
  LoginFullResponse,
  LoginSource,
  setDocDivisionAccess,
  setUser,
  openSrmManagement,
  setTokens,
  switchInstance,
  getLoginError,
  getLoginSource,
  getSelectedLoginTo,
  getTisRegistrationUrl,
  getUserStatus
} from "./loginUtils";
import { useSelector } from "react-redux";
import isEmpty from "lodash/isEmpty";
import { setCompressedItem } from "helpers/compressedStorage";
import {
  setCurrentUser,
  generateNewPermissionData,
  generateOldPermissionData,
  logout
} from "actions/authActions";

const preventDefault = (callback, loginSource) => e => {
  e.preventDefault();
  callback(loginSource);
};

enum SCS_LOGINS {
  SCS = "SCS",
  ME = "ME",
  CUSTOMER = "CUSTOMER"
}

const getAllowedLoginSource = user => {
  const loginSource =
    user?.compperms?.login_source || user?.comp_perms?.login_source;
  const gatewayPermission = user?.is_gateway_admin || user?.enable_gateway;
  if (loginSource === LoginSource.SRM && gatewayPermission) {
    return LoginSource.BOTH;
  }
  return loginSource || LoginSource.BOTH;
};

const VerifyLoginUser = () => {
  const { companyId } = useParams();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const user = useSelector<ReduxState, any>(state => state?.auth?.user);
  const isLoggedIn = useSelector<ReduxState, boolean[]>(
    state => state.auth.isAuthenticated
  );
  const [allowedLoginSources, setAllowedLoginSources] = useState(
    getAllowedLoginSource(user)
  );
  const [loginSource, setLoginSource] = useState("");
  const [search, setSearch] = useState("");
  const [selectedDivision, setSelectedDivision] = useState(null);
  const [divisionOptions, setDivisionOptions] = useState([]);
  const [showLoginAs, setShowLoginAs] = useState(false);
  const [canShowMe, setCanShowMe] = useState(false);
  const [loginAs, setLoginAs] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [scsEmail, setScsEmail] = useState("");
  const [operatorByDivision, setOperatorByDivision] = useState(false);
  const [displayAlternateDivisionNumber, setDisplayAlternateDivisionNumber] =
    useState(
      user?.systemInfo?.display_alternate_division_number === SrmBoolean.True
    );
  const [errors, setErrors] = useState<{
    loginSource?: string;
    selectedDivision?: string;
    selectedLocation?: string;
    customerEmail?: string;
  }>({});
  const [isLoading, setLoading] = useState(false);
  const [isSyncingData, setIsSyncingData] = useState(true);
  const [ConfirmModalLogin, showConfirmModal] = useConfirmModal();
  const [loginConfirmed, setLoginComfirmed] = useState(false);
  const mobileCallbackUrl = query.get("callbackUrl");

  useEffect(() => {
    if (companyId) {
      setLoginSource(LoginSource.TW);
      return;
    }
    if (allowedLoginSources === LoginSource.BOTH) {
      setLoginSource(localStorage.getItem("loginSource"));
      return;
    }
    setLoginSource(allowedLoginSources);
  }, [allowedLoginSources, companyId]);

  useEffect(() => {
    let tenant = null;
    if (query.get("tenant")) {
      tenant = query.get("tenant");
    }

    if (user && localStorage.jwtToken) {
      validateLoggedInUser();
      return;
    }

    if (!tenant) return;
    validateInternalUser(tenant);
  }, []);

  const validateLoggedInUser = async () => {
    if (user.is_tis && user.company_sso_enabled) {
      try {
        await fleetlogicAPI.post(`${TIS_SESSION_VERIFY_URL}/`, {
          internal: true
        });
      } catch (err) {
        reduxStore.dispatch(
          logout(
            `${window.location.origin}${mobileCallbackUrl ? `?callbackUrl=${mobileCallbackUrl}` : ""}`
          )
        );
        return;
      }
    }
    if (mobileCallbackUrl) {
      redirectToMobile(user);
      return;
    }

    if (query.get("applicationId")) {
      handleSubmit(getSelectedLoginTo(query.get("applicationId")), true);
      return;
    }
    const isScsUser = user?.srm_uid === SCS_SRM_UID;
    let userDivisions = isScsUser
      ? user?.divisions || []
      : user?.login_divisions || user?.divisions || [];
    if (user?.upgraded && isScsUser) {
      userDivisions = user?.divisions?.filter(e => e.is_user_division) || [];
    }
    if (user?.compperms?.operator_by_division) {
      setIsSyncingData(false);
      fetchDivisions(user, userDivisions);
      return;
    }
    fetchDivisions(user, userDivisions);
    setIsSyncingData(false);
  };

  const validateInternalUser = async tenant => {
    try {
      const res = await fleetlogicAPI.post(
        `${TIS_SESSION_VERIFY_URL}/${tenant}`,
        {
          internal: true
        }
      );
      const data = res.data;
      let divisions = data?.is_texada_user
        ? data?.divisions || []
        : data?.login_divisions || [];

      setAllowedLoginSources(getAllowedLoginSource(data));

      if (data?.comp_perms?.operator_by_division) {
        fetchDivisions(data, divisions);
      } else {
        fetchDivisions(data, divisions);
      }
      setOperatorByDivision(!!data?.comp_perms?.operator_by_division);
      setDisplayAlternateDivisionNumber(
        data?.system_info?.display_alternate_division_number === SrmBoolean.True
      );

      const shouldShowLoginAs = !!data?.is_texada_user;
      setShowLoginAs(shouldShowLoginAs);
      setCanShowMe(!!data?.is_company_user);
      setScsEmail(data?.scs_email || "");

      if (shouldShowLoginAs) {
        if (data?.scs_email) {
          setLoginAs(SCS_LOGINS.SCS);
        } else if (data?.is_company_user) {
          setLoginAs(SCS_LOGINS.ME);
        } else {
          setLoginAs(SCS_LOGINS.CUSTOMER);
        }
      } else if (mobileCallbackUrl) {
        await validateSession(tenant);
        return;
      }
      setIsSyncingData(false);
    } catch (err) {
      if (!isLoggedIn) {
        if (mobileCallbackUrl) {
          window.location.href = new Uri(mobileCallbackUrl)
            .addQueryParam("error", getLoginError(err))
            .toString();
          return;
        }
        localStorage.setItem("login-error", getLoginError(err));
        window.location.href = window.location.origin;
      }
      throw err;
    }
  };

  const validateSession = async (
    tenant,
    chosenEmail?: string,
    selectedLoginSource?: string
  ) => {
    let tokenRes;
    try {
      const body: any = {
        source: getLoginSource(query.get("source"), selectedLoginSource)
      };
      if (chosenEmail) body.email = chosenEmail;

      tokenRes = await fleetlogicAPI.post(
        `${TIS_SESSION_VERIFY_URL}/${tenant}?is_login=true`,
        body
      );
      await setTokens(tokenRes.data);
      AuthorizationToken.set(tokenRes.data.token);

      if (!tokenRes.data.company.upgraded) {
        const userInfo = await setNonUpgradedUserData(tokenRes.data);
        return { user: userInfo, isFailed: false };
      }

      if (
        Number(tokenRes.data.user?.userinstance?.current.id) !== Number(tenant)
      ) {
        await switchInstance(
          tokenRes.data.user.id,
          tokenRes.data.user?.userinstance?.current.id,
          tenant
        );
        tokenRes = await fleetlogicAPI.post(
          `${TIS_SESSION_VERIFY_URL}/${tenant}`,
          {
            source: getLoginSource(query.get("source"), selectedLoginSource)
          }
        );
        await setTokens(tokenRes.data);
        AuthorizationToken.set(tokenRes.data.token);
      }
      const userInfo = await setUpgradedUserData(tokenRes.data);
      return { user: userInfo, isFailed: false };
    } catch (err) {
      setErrors({ customerEmail: getLoginError(err) });
      return { user, isFailed: true };
    }
  };

  const setUpgradedUserData = async userData => {
    const companyPermissionRes = await fleetlogicAPI.get(
      `${COMPANY_PERMISSION_URL}${userData.user.company}`
    );
    const userRes = await fleetlogicAPI.get(
      `${USER_URL}${userData.user.id}?include_flask=true`
    );
    const permissionRes = await fleetlogicAPI.get(
      `${USER_PERMISSION_URL}${userData.user.id}`
    );
    const permissions = generateNewPermissionData(
      companyPermissionRes.data,
      permissionRes.data.divisions,
      permissionRes.data.locations
    );

    let userInfo = {
      ...userData,
      user: userRes.data,
      srm_uid: userRes.data.srm_uid,
      comp_perms: companyPermissionRes.data,
      permissions: permissions.permissions,
      locations: permissions.locations,
      divisions: permissions.divisions,
      useTexadaPay: companyPermissionRes.data.use_texada_pay,
      companyId: userData.user.company,
      upgraded: companyPermissionRes.data.upgraded,
      isSuperAdmin: permissionRes.data.is_super_admin,
      is_operator: userRes.data.is_support
    };
    userInfo = await setUser(userInfo as LoginFullResponse);

    if (mobileCallbackUrl) {
      await redirectToMobile(userInfo);
      return userInfo;
    }

    if (query.get("applicationId")) {
      await handleSubmit(getSelectedLoginTo(query.get("applicationId")), true);
      return userInfo;
    }
    return userInfo;
  };

  const setNonUpgradedUserData = async userData => {
    const { user, comp_perms } = userData;
    const permissions = generateOldPermissionData(
      comp_perms,
      userData.divisions,
      userData.locations
    );

    let userInfo = {
      ...userData,
      isSuperAdmin: userData.user.is_super_admin,
      srm_uid: userData.user.srm_uid,
      permissions: {
        ...permissions.permissions,
        admin: user.settings_menu_access,
        update_settings: user.settings_menu_access,
        capture_signatures: user.capture_signatures,
        change_asset_status: user.change_asset_status,
        maintain_sites: user.maintain_sites,
        update_meter_reading: user.update_meter_reading,
        view_inventory: user.view_inventory,
        workorder_default_reporting: user.workorder_default_reporting
      },
      locations: permissions.locations,
      divisions: permissions.divisions,
      companyId: userData.user.company,
      upgraded: userData.company.upgraded
    };

    userInfo = await setUser(userInfo as LoginFullResponse);
    if (mobileCallbackUrl) {
      await redirectToMobile(userInfo);
      return userInfo;
    }

    if (query.get("applicationId")) {
      await handleSubmit(getSelectedLoginTo(query.get("applicationId")), true);
      return userInfo;
    }
    return userInfo;
  };

  const fetchDivisions = async (user, divisions) => {
    const newDivisionList = divisions.map(item => {
      const baseWarehouse = item?.warehouse || {};
      const loginWarehouse = item?.login_warehouse || baseWarehouse;
      const isScsUser = !item.hasOwnProperty("login_warehouse");

      return {
        ...loginWarehouse,
        srm_uid: loginWarehouse?.srm_uid || baseWarehouse?.srm_uid,
        title: loginWarehouse?.title || baseWarehouse?.title,
        value: isScsUser
          ? baseWarehouse?.srm_uid
          : item?.DivLocId || HEADOFFICE_OPTION_VALUE,
        alternate_division_number: loginWarehouse?.alternate_division_number,
        operator_code: item?.DivLocId?.split(":")?.[2]?.trim() || "",
        label: `[${
          user?.systemInfo?.display_alternate_division_number ===
            SrmBoolean.True &&
          (loginWarehouse?.alternate_division_number ||
            baseWarehouse?.alternate_division_number)
            ? loginWarehouse?.alternate_division_number || "\xa0".repeat(4)
            : loginWarehouse?.srm_uid || "\xa0".repeat(4)
        }${
          !isScsUser && item?.DivLocId?.split(":")?.[2]?.trim()
            ? ` - ${item?.DivLocId?.split(":")?.[2]?.trim()}`
            : ""
        }] — ${baseWarehouse?.title}`,
        div_loc_id: item.DivLocId
      };
    });
    setDivisionOptions(newDivisionList);
    setSearch("");

    const docAccess = JSON.parse(localStorage.getItem("docDivisionAccess"));
    if (isEmpty(docAccess?.[user.tenant_id])) return;
    const selectedDivisionSrmUid = localStorage.getItem(
      "selectedDivisionSrmUid"
    );
    const docDivision = docAccess[user.tenant_id].divisions.find(e =>
      selectedDivisionSrmUid !== null && selectedDivisionSrmUid !== "undefined"
        ? e.srm_uid === selectedDivisionSrmUid ||
          e.div_loc_id === selectedDivisionSrmUid
        : e.is_doc_division
    );
    const target = [...(divisions || []), ...(user?.divisions || [])]?.find(
      e => e.warehouse.srm_uid === docDivision?.srm_uid
    );

    if (!target) return;

    setSelectedDivision({
      srm_uid: target.warehouse.srm_uid,
      label: target.warehouse.title,
      value: target.DivLocId || HEADOFFICE_OPTION_VALUE,
      div_loc_id: target.DivLocId || ""
    });
  };

  const validate = () => {
    const newErrors: {
      loginSource?: string;
      selectedDivision?: string;
      selectedLocation?: string;
      customerEmail?: string;
    } = {};

    if (isEmpty(loginSource)) {
      newErrors.loginSource = i18n.t("field_is_required", {
        field: i18n.t("LoginSource")
      });
    }
    if (
      (operatorByDivision || user?.compperms?.operator_by_division) &&
      isEmpty(selectedDivision)
    ) {
      newErrors.selectedDivision = i18n.t("field_is_required", {
        field: i18n.t("division")
      });
    }
    if (
      showLoginAs &&
      loginAs === SCS_LOGINS.CUSTOMER &&
      isEmpty(customerEmail)
    ) {
      newErrors.customerEmail = i18n.t("field_is_required", {
        field: i18n.t("email")
      });
    }

    setErrors(newErrors);
    return newErrors;
  };

  const handleSubmit = async (loginOption, skipValidation = false) => {
    const newErrors = validate();
    if (!isEmpty(newErrors) && !skipValidation && !mobileCallbackUrl) return;

    if (loginOption === LoginSource.SRM && !loginConfirmed && !skipValidation) {
      if (allowedLoginSources !== LoginSource.SRM) {
        const shouldProceed = await showConfirmModal();
        setLoginComfirmed(shouldProceed);
        if (!shouldProceed) {
          return;
        }
      }
    }

    let emailToUse: string | undefined = undefined;
    if (showLoginAs) {
      if (loginAs === SCS_LOGINS.SCS) emailToUse = scsEmail || undefined;
      if (loginAs === SCS_LOGINS.CUSTOMER)
        emailToUse = customerEmail || undefined;
    }
    setLoading(true);
    const tenant = query.get("tenant");
    const { user: loggedInUserInfo, isFailed } = await validateSession(
      tenant,
      emailToUse,
      loginOption
    );
    if (showLoginAs && isFailed) {
      setLoading(false);
      return;
    }
    if (mobileCallbackUrl) {
      return;
    }
    let tisRegistionUrl = null;
    if (user?.email) {
      const { email_verified: emailVerified } = await getUserStatus(
        user?.email
      );
      tisRegistionUrl = await getTisRegistrationUrl(user, emailVerified, true);
    }

    const finalUser = loggedInUserInfo || user;
    const isScsUser = finalUser?.srm_uid === SCS_SRM_UID;
    localStorage.setItem("loginSource", loginOption);
    localStorage.setItem("verified", true);
    localStorage.setItem("currentTenant", tenant);
    localStorage.removeItem("login-error");
    if (selectedDivision?.srm_uid !== undefined) {
      localStorage.setItem(
        "selectedDivisionSrmUid",
        isScsUser ? selectedDivision?.srm_uid : selectedDivision?.div_loc_id
      );
    }

    let userWithDocAccess = finalUser;
    try {
      userWithDocAccess = await setDocDivisionAccess(
        finalUser,
        selectedDivision?.srm_uid,
        isScsUser ? undefined : selectedDivision?.div_loc_id
      );
    } catch (err) {
      console.error("Error setting document division access:", err);
    }

    if (loginOption === LoginSource.SRM && !mobileCallbackUrl) {
      setCompressedItem("currentUser", JSON.stringify(userWithDocAccess));
      let divLocIdQuery = isScsUser ? "" : selectedDivision?.div_loc_id;
      if (tisRegistionUrl) {
        window.location.href = `${tisRegistionUrl}?callbackUrl=${LoginSource.SRM}&division=${selectedDivision?.srm_uid}${divLocIdQuery ? `&div_loc_id=${divLocIdQuery}` : ""}`;
        return;
      }

      await openSrmManagement(
        loggedInUserInfo?.compperms?.operator_by_division ||
          user?.compperms?.operator_by_division,
        selectedDivision?.srm_uid,
        divLocIdQuery
      );
      setLoading(false);
      return;
    }

    await reduxStore.dispatch(setCurrentUser(userWithDocAccess));
    window.location.href = tisRegistionUrl
      ? tisRegistionUrl
      : APP_DEFAULT_REDIRECT_URL;
    setLoading(false);
  };

  const redirectToMobile = async user => {
    let mobileRedirectUrl = new Uri(mobileCallbackUrl).addQueryParam(
      "token",
      localStorage.jwtToken
    );
    mobileRedirectUrl.addQueryParam("env", window.location.hostname);
    const userInfo = await setDocDivisionAccess(user);
    await reduxStore.dispatch(setCurrentUser(userInfo));
    localStorage.removeItem("mobileRedirectUrl");
    localStorage.setItem("verified", true);
    const { email_verified: emailVerified } = await getUserStatus(user?.email);
    const tisRegistionUrl = await getTisRegistrationUrl(
      user,
      emailVerified,
      true
    );
    window.location.href = tisRegistionUrl
      ? `${tisRegistionUrl}?callbackUrl=${mobileRedirectUrl.toString()}`
      : mobileRedirectUrl.toString();
  };

  const loginSourceOptions = useMemo(() => {
    switch (allowedLoginSources) {
      case LoginSource.SRM:
        return [
          {
            label: i18n.t("srf_classic"),
            value: LoginSource.SRM
          }
        ];
      case LoginSource.TW:
        return [
          {
            label: i18n.t("srf_next"),
            value: LoginSource.TW
          }
        ];
      default:
        return [
          {
            label: i18n.t("srf_next"),
            value: LoginSource.TW
          },
          {
            label: i18n.t("srf_classic"),
            value: LoginSource.SRM
          }
        ];
    }
  }, [allowedLoginSources]);

  if (isSyncingData)
    return <SRMSyncLoader message={i18n.t("validating_user_please_wait")} />;

  const finalDivisionOptions = filterSortMapOptions(
    divisionOptions,
    search,
    ["srm_uid", "operator_code", "title", "label", "alternate_division_number"],
    "label",
    false,
    divisionOption => ({
      ...divisionOption,
      value: divisionOption.value || HEADOFFICE_OPTION_VALUE
    })
  );

  return (
    <>
      <ConfirmModalLogin
        header={`${i18n.t("login_to")} ${i18n.t("srf_classic")}`}
        message={i18n.t(
          "by_choosing_login_to_srf_classic_this_page_will_redirect_to_classic_ui"
        )}
        confirmText={i18n.t("proceed")}
        cancelText={i18n.t("cancel")}
      />
      {allowedLoginSources === LoginSource.SRM && user && (
        <p className="login-info">
          {i18n.t("company")}: {user?.company_title} <br />
          {i18n.t("email")}: {user?.email}
        </p>
      )}
      <form
        className="login_form"
        onSubmit={e => preventDefault(handleSubmit, loginSource)(e)}
      >
        {!mobileCallbackUrl && (
          <>
            <Select
              label={i18n.t("login_to")}
              className={`password ${isPlayEnv() ? "play" : ""}`}
              required
              value={loginSource}
              options={loginSourceOptions}
              onChange={e => {
                setLoginSource(e.target.value);
              }}
              error={errors?.loginSource}
            />

            {(operatorByDivision ||
              user?.compperms?.operator_by_division ||
              divisionOptions.length > 1) && (
              <Select
                label={i18n.t("division")}
                required
                placeholder={i18n.t("select_document_division")}
                value={selectedDivision?.value}
                options={finalDivisionOptions}
                shouldFilter={false}
                onInputChange={value => setSearch(value)}
                onChange={selected => {
                  const target = finalDivisionOptions.find(
                    division =>
                      (division.value ?? "").trim() ===
                      (selected.target.value ?? "").trim()
                  );
                  setSelectedDivision(
                    target
                      ? {
                          ...target,
                          value: selected.target.value
                        }
                      : null
                  );
                }}
                error={errors?.selectedDivision}
                clearable={true}
              />
            )}
          </>
        )}

        {showLoginAs && (
          <div className="login-as-group">
            <Radio.Group
              label={i18n.t("login_as")}
              value={loginAs}
              onChange={e => {
                setLoginAs(e.currentTarget.value);
              }}
            >
              {scsEmail && (
                <Radio.Button
                  label={SCS_LOGINS.SCS}
                  value={SCS_LOGINS.SCS}
                  key={SCS_LOGINS.SCS}
                />
              )}
              {canShowMe && (
                <Radio.Button
                  label={SCS_LOGINS.ME}
                  value={SCS_LOGINS.ME}
                  key={SCS_LOGINS.ME}
                />
              )}
              <Radio.Button
                label={SCS_LOGINS.CUSTOMER}
                value={SCS_LOGINS.CUSTOMER}
                key={SCS_LOGINS.CUSTOMER}
              />
            </Radio.Group>
            {loginAs === SCS_LOGINS.CUSTOMER && (
              <Input
                className={`email ${isPlayEnv() ? "play" : ""}`}
                placeholder={i18n.t("please_enter_customer_email")}
                field="email"
                value={customerEmail}
                error={errors?.customerEmail}
                onChange={e => setCustomerEmail(e.target.value)}
              />
            )}
          </div>
        )}

        <Button
          className="login-button"
          isLoading={isLoading}
          buttonProps={{ type: "submit" }}
        >
          {i18n.t("login")}
        </Button>
      </form>
    </>
  );
};

export default VerifyLoginUser;
